import { useState, useEffect } from "react";
import { api, Project, ProjectUser } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Loader2, X, Users, RefreshCw } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useConfirm } from "@/lib/confirmContext";
import { useAuth } from "@/lib/authContext";
import { RECURRENCE_OPTIONS } from "@/lib/recurrenceOptions";

interface ProjectEditDialogProps {
  project: Project;
  onClose: () => void;
  onSaved?: (updated: Project) => void;
}

export function ProjectEditDialog({ project, onClose, onSaved }: ProjectEditDialogProps) {
  const { toast } = useToast();
  const askConfirm = useConfirm();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [spawning, setSpawning] = useState(false);
  const [assignedUsers, setAssignedUsers] = useState<ProjectUser[]>([]);
  const [allUsers, setAllUsers] = useState<{ id: string; name: string; email: string }[]>([]);
  const [newUserId, setNewUserId] = useState("");
  const [newUserRole, setNewUserRole] = useState("agent");
  const [addingUser, setAddingUser] = useState(false);

  type ProjectForm = {
    title: string; status: string; start_date: string; due_date: string; internal_due_date: string;
    recurring_pattern: string; next_spawn_date: string; project_type: string; tax_year: string; period_label: string;
  };
  // The form as last loaded or saved, so spawning can tell whether there are unsaved edits (ENG-115).
  const [savedForm, setSavedForm] = useState<ProjectForm | null>(null);
  const [form, setForm] = useState<ProjectForm>({
    title: project.title || "",
    status: project.status || "open",
    start_date: project.start_date || "",
    due_date: project.due_date || "",
    internal_due_date: (project as any).internal_due_date || "",
    recurring_pattern: project.recurring_pattern || "",
    next_spawn_date: (project as any).next_spawn_date || "",
    project_type: project.project_type || "",
    tax_year: project.tax_year ? String(project.tax_year) : "",
    period_label: project.period_label || "",
  });

  useEffect(() => {
    const load = async () => {
      try {
        const [full, users] = await Promise.all([
          api.getProject(project.id),
          api.getUsers(),
        ]);
        setAssignedUsers(full.assigned_users || []);
        setAllUsers(users.map((u: any) => ({ id: u.id, name: u.name || u.email, email: u.email })));
        const loaded: ProjectForm = {
          title: full.title || "",
          status: full.status || "open",
          start_date: full.start_date || "",
          due_date: full.due_date || "",
          internal_due_date: (full as any).internal_due_date || "",
          recurring_pattern: full.recurring_pattern || "",
          next_spawn_date: (full as any).next_spawn_date || "",
          project_type: full.project_type || "",
          tax_year: full.tax_year ? String(full.tax_year) : "",
          period_label: full.period_label || "",
        };
        setForm(loaded);
        setSavedForm(loaded);
      } catch {
        toast({ title: "Failed to load project", variant: "destructive" });
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [project.id]);

  const buildPayload = () => ({
    title: form.title,
    status: form.status,
    start_date: form.start_date || null,
    due_date: form.due_date || null,
    internal_due_date: form.internal_due_date || null,
    recurring_pattern: form.recurring_pattern || null,
    next_spawn_date: form.next_spawn_date || null,
    project_type: form.project_type || null,
    tax_year: form.tax_year ? Number(form.tax_year) : null,
    period_label: form.period_label || null,
  } as any);

  const isDirty = !!savedForm && (Object.keys(form) as (keyof ProjectForm)[]).some(k => form[k] !== savedForm[k]);

  const handleSave = async () => {
    if (!form.title.trim()) return;
    setSaving(true);
    try {
      const updated = await api.updateProject(project.id, buildPayload());
      toast({ title: "Project saved" });
      onSaved?.(updated);
      onClose();
    } catch {
      toast({ title: "Failed to save project", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const handleAddUser = async () => {
    if (!newUserId) return;
    setAddingUser(true);
    try {
      const added = await api.addProjectUser(project.id, newUserId, newUserRole);
      setAssignedUsers(prev => [...prev, added]);
      setNewUserId("");
      setNewUserRole("agent");
    } catch {
      toast({ title: "Failed to add user", variant: "destructive" });
    } finally {
      setAddingUser(false);
    }
  };

  const handleRemoveUser = async (userId: string) => {
    try {
      await api.removeProjectUser(project.id, userId);
      setAssignedUsers(prev => prev.filter(u => u.user_id !== userId));
    } catch {
      toast({ title: "Failed to remove user", variant: "destructive" });
    }
  };

  // The server clones the SAVED project, so unsaved edits are saved first; otherwise
  // a title typed here never reaches the successor and is lost on close (ENG-115).
  const handleSpawnNow = async () => {
    if (isDirty && !form.title.trim()) {
      toast({ title: "Enter a title before spawning", variant: "destructive" });
      return;
    }
    if (!(await askConfirm({
      title: "Create the next occurrence now?",
      subject: "project",
      name: isDirty ? form.title : (savedForm?.title ?? form.title),
      body: `will spawn its next ${form.recurring_pattern} occurrence immediately.`,
      lines: [
        ...(isDirty ? ["Your unsaved changes will be saved first."] : []),
        "This is in addition to the scheduled one, not instead of it.",
      ],
      confirmLabel: isDirty ? "Save and create it now" : "Create it now",
    }))) return;
    setSpawning(true);
    let updated: Project = project;
    if (isDirty) {
      try {
        updated = await api.updateProject(project.id, buildPayload());
        setSavedForm(form);
      } catch {
        toast({ title: "Failed to save project", description: "Nothing was spawned.", variant: "destructive" });
        setSpawning(false);
        return;
      }
    }
    try {
      await api.spawnProjectNow(project.id);
      toast({ title: "Successor project created" });
      onClose();
    } catch (err: any) {
      toast({ title: "Spawn failed", description: err.message, variant: "destructive" });
    } finally {
      // Refresh the page either way: the successor and/or the saved edits are new.
      onSaved?.(updated);
      setSpawning(false);
    }
  };

  const availableUsers = allUsers.filter(u => !assignedUsers.some(au => au.user_id === u.id));
  const periodTypes = ["Bookkeeping", "Financial Statements", "Payroll", "Sales Tax", "Property Tax"];

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Project</DialogTitle>
          <DialogDescription>Update project details and team assignments.</DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="space-y-4 py-2">
            {/* Title */}
            <div className="space-y-2">
              <Label>Title <span className="text-destructive">*</span></Label>
              <Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} autoFocus />
            </div>

            {/* Status */}
            <div className="space-y-2">
              <Label>Status</Label>
              <Select value={form.status} onValueChange={v => setForm({ ...form, status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="open">Open</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="on_hold">On Hold</SelectItem>
                  <SelectItem value="closed">Closed</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Dates */}
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-2">
                <Label>Start Date</Label>
                <Input type="date" value={form.start_date} onChange={e => setForm({ ...form, start_date: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Client Due</Label>
                <Input type="date" value={form.due_date} onChange={e => setForm({ ...form, due_date: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Internal Due</Label>
                <Input type="date" value={form.internal_due_date} onChange={e => setForm({ ...form, internal_due_date: e.target.value })} />
              </div>
            </div>

            {/* Project Type + Year + Month */}
            <div className="space-y-2">
              <Label>Project Type</Label>
              <Select value={form.project_type || "none"} onValueChange={v => setForm({ ...form, project_type: v === "none" ? "" : v })}>
                <SelectTrigger><SelectValue placeholder="Select type..." /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— None —</SelectItem>
                  {["1040","1120S","1065","1120","1041","990","Bookkeeping","Financial Statements","Property Tax","Sales Tax","Payroll","Advisory","Other"].map(t => (
                    <SelectItem key={t} value={t}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Year</Label>
                <Select value={form.tax_year || "none"} onValueChange={v => setForm({ ...form, tax_year: v === "none" ? "" : v })}>
                  <SelectTrigger><SelectValue placeholder="Year..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— None —</SelectItem>
                    {[2026, 2025, 2024, 2023, 2022].map(y => (
                      <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {form.project_type && periodTypes.includes(form.project_type) ? (
                <div className="space-y-2">
                  <Label>Month</Label>
                  <Select value={form.period_label || "none"} onValueChange={v => setForm({ ...form, period_label: v === "none" ? "" : v })}>
                    <SelectTrigger><SelectValue placeholder="Month..." /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— None —</SelectItem>
                      {["01","02","03","04","05","06","07","08","09","10","11","12"].map((m, i) => (
                        <SelectItem key={m} value={m}>{m} — {["January","February","March","April","May","June","July","August","September","October","November","December"][i]}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ) : <div />}
            </div>

            {/* Recurrence */}
            <div className="space-y-2">
              <Label>Recurrence</Label>
              <Select value={form.recurring_pattern || "none"} onValueChange={v => setForm({ ...form, recurring_pattern: v === "none" ? "" : v })}>
                <SelectTrigger><SelectValue placeholder="One-time" /></SelectTrigger>
                <SelectContent>
                  {RECURRENCE_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {form.recurring_pattern && form.recurring_pattern !== "none" && (
              <div className="rounded-md border border-blue-200 bg-blue-50 p-3 space-y-3">
                <div className="flex items-center gap-2">
                  <RefreshCw className="w-3.5 h-3.5 text-blue-600" />
                  <span className="text-sm font-semibold text-blue-800 capitalize">↺ {form.recurring_pattern} project</span>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-blue-700">Next Spawn Date</Label>
                  <Input
                    type="date"
                    value={form.next_spawn_date}
                    onChange={e => setForm({ ...form, next_spawn_date: e.target.value })}
                    className="bg-white border-blue-300"
                  />
                  <p className="text-[11px] text-blue-600">The scheduler auto-spawns on this date. You can also spawn immediately below.</p>
                </div>
                {user && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleSpawnNow}
                    disabled={spawning}
                    className="border-blue-400 text-blue-700 hover:bg-blue-100 w-full"
                  >
                    {spawning ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <RefreshCw className="w-3.5 h-3.5 mr-1.5" />}
                    Spawn Next Instance Now
                  </Button>
                )}
              </div>
            )}

            {/* Team */}
            <Separator className="my-2" />
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4" />
                <h3 className="font-semibold text-sm">Team</h3>
              </div>

              {assignedUsers.length > 0 && (
                <div className="space-y-1.5">
                  {assignedUsers.map(au => (
                    <div key={au.id} className="flex items-center justify-between px-3 py-2 bg-muted/50 rounded-lg">
                      <div>
                        <p className="text-sm font-medium">{(au as any).users?.name || (au as any).users?.email || allUsers.find(u => u.id === au.user_id)?.name || "Unknown"}</p>
                        <p className="text-xs text-muted-foreground capitalize">{au.role}</p>
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => handleRemoveUser(au.user_id)}>
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}

              {availableUsers.length > 0 && (
                <div className="flex gap-2">
                  <Select value={newUserId} onValueChange={setNewUserId}>
                    <SelectTrigger className="flex-1"><SelectValue placeholder="Select user..." /></SelectTrigger>
                    <SelectContent>
                      {availableUsers.map(u => (
                        <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={newUserRole} onValueChange={setNewUserRole}>
                    <SelectTrigger className="w-28"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="agent">Agent</SelectItem>
                      <SelectItem value="manager">Manager</SelectItem>
                      <SelectItem value="reviewer">Reviewer</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button variant="outline" onClick={handleAddUser} disabled={!newUserId || addingUser}>
                    {addingUser ? <Loader2 className="w-4 h-4 animate-spin" /> : "Add"}
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} disabled={!form.title.trim() || saving || loading}>
            {saving ? <Loader2 className="mr-2 w-4 h-4 animate-spin" /> : null}
            Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
